import { createFileRoute } from '@tanstack/react-router';
import { useTranslate } from '@tolgee/react';
import { AppRoute } from '@agentselly/immodossier-shared';
import { useLoaderQuery } from '~/helper/useLoaderQuery';
import { useAppParams } from '~/helper/useAppParams';
import { LoadingWidget } from '~/components/widget/base/LoadingWidget';
import { ErrorWidget } from '~/components/widget/base/ErrorWidget';
import { RenderWidgets } from '~/components/widget/RenderWidgets';
import { cockpitOffersWidgets } from '~/config/widgets/cockpit/cockpitOffersWidgets';
import { AppLink } from '~/components/AppLink';
import { useAuthz } from '~/context/AuthzContext';
import {
  Bars,
  ContactActions,
  EmptyBlock,
  fmtChf,
  fmtDate,
  Ico,
  Kpi,
  NewBadge,
  PageHead,
  TrendChart,
  V3Page,
} from '~/components/cockpit/v3';
import type { WidgetProps } from '~/components/cockpit/cockpitData';

export const Route = createFileRoute(
  AppRoute.$LANG_$COMPANYID_$CONTACTID_COCKPIT_$PROPERTYID_OFFERS,
)({
  component: OffersPage,
});

interface Offer {
  readonly date: string;
  readonly offerPrice: string;
  readonly person: string;
  readonly personEmail?: string;
  readonly personPhone?: string;
  readonly message?: string;
  readonly offerConfirmationSecureAssetsPath?: string;
}

const toNum = (v: unknown): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/** Estimated value: newest evaluation, else iazi source, else midpoint of the range. */
function estimate(wp: WidgetProps): { value: number; date?: string } {
  const welcome = wp.cockpitWelcome ?? {};
  const ev = welcome.property?.evaluation;
  const range = wp.cockpitOffersDetailWidget?.evaluation;
  const lo = toNum(range?.estimatedLowerValue);
  const hi = toNum(range?.estimatedUpperValue);
  return {
    value:
      toNum(welcome.evaluationTrend?.evaluationValue) ||
      toNum(ev?.sources?.iazi?.estimatedValue) ||
      (lo && hi ? Math.round((lo + hi) / 2) : 0),
    date: welcome.evaluationTrend?.evaluationDate || ev?.timestamp,
  };
}

function OffersPage(): React.ReactElement {
  const params = useAppParams(Route);
  const { t } = useTranslate();
  const { atLeast } = useAuthz();
  const isAdvisor = atLeast('partner');

  const query = useLoaderQuery(
    AppRoute.$LANG_$COMPANYID_$CONTACTID_COCKPIT_$PROPERTYID_OFFERS,
    { params },
  );

  if (query.isLoading) return <LoadingWidget />;
  if (query.isError || !query.data) return <ErrorWidget error={query.error} />;

  if (!query.data.v3Redesign) {
    const widgetProps = query.data?.widgetProps || {};
    const widgets = cockpitOffersWidgets(widgetProps);
    return (
      <div className="h-full flex flex-col gap-y-6">
        <RenderWidgets widgets={widgets} />
      </div>
    );
  }

  const wp = (query.data.widgetProps || {}) as WidgetProps;
  const oldestFirst: Offer[] = [
    ...(wp.cockpitOffersInDetailWidget?.data ?? []),
  ].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  const secureUrl: string =
    wp.cockpitOffersInDetailWidget?.secureAssetsUrl ?? '';
  const asking =
    toNum(wp.cockpitOffersDetailWidget?.price) ||
    toNum(wp.cockpitWelcome?.property?.price);
  const top = Math.max(0, ...oldestFirst.map((o) => toNum(o.offerPrice)));
  const est = estimate(wp);
  const diff = asking ? ((top - asking) / asking) * 100 : null;

  const head = (
    <PageHead
      odId="offers-head"
      title={t('cockpit_offers_title', 'Angebote')}
      sub={`${t('cockpit_offers_sub', 'Verkaufspreis')} ${fmtChf(asking)}`}
    />
  );
  const estKpi = (
    <Kpi
      odId="kpi-estimate"
      current
      value={fmtChf(est.value)}
      label={t('cockpit_offers_kpi_estimate', 'Geschätzter Wert')}
      note={
        est.date
          ? `${t('cockpit_offers_eval_from', 'Bewertung vom')} ${fmtDate(
              est.date,
            )}`
          : t('cockpit_offers_from_eval', 'aus der Bewertung')
      }
    />
  );

  if (oldestFirst.length === 0) {
    return (
      <V3Page>
        {head}
        <div className="grid g2">
          <Kpi
            odId="kpi-count"
            value={0}
            label={t('cockpit_offers_kpi_count', 'Angebote')}
          />
          {estKpi}
        </div>
        <EmptyBlock
          icon="coin"
          title={t('cockpit_offers_empty_title', 'Noch keine Angebote')}
          text={t(
            'cockpit_offers_empty_text',
            'Sobald ein Interessent ein Angebot abgibt, sehen Sie hier Betrag, Datum und die Angebotsbestätigung – und wie sich die Angebote im Vergleich zum geschätzten Wert entwickeln.',
          )}
          actions={
            <AppLink
              to={
                AppRoute.$LANG_$COMPANYID_$CONTACTID_COCKPIT_$PROPERTYID_INTEREST
              }
              params={params}
              className="btn"
              data-od-id="cta-to-interest"
            >
              <Ico
                name="users"
                size={16}
              />
              {t('cockpit_offers_to_interest', 'Zu den Interessenten')}
            </AppLink>
          }
        />
      </V3Page>
    );
  }

  const points = oldestFirst.map((o) => ({
    date: o.date,
    value: toNum(o.offerPrice),
  }));
  const trendAlt = `${t(
    'cockpit_offers_trend_alt',
    'Angebotsverlauf',
  )}: ${points.map((p) => `${fmtDate(p.date)} ${fmtChf(p.value)}`).join(', ')}${
    est.value
      ? `. ${t('cockpit_offers_kpi_estimate', 'Geschätzter Wert')} ${fmtChf(
          est.value,
        )}`
      : ''
  }`;

  return (
    <V3Page>
      {head}
      <div
        className="grid g3"
        data-od-id="kpi-row"
        data-widget="cockpitOffersTrendWidget"
      >
        <Kpi
          odId="kpi-count"
          value={oldestFirst.length}
          delta={wp.cockpitOffers?.changeInLastWeek}
          label={t('cockpit_offers_kpi_count', 'Angebote')}
        />
        <Kpi
          odId="kpi-top"
          current
          value={fmtChf(top)}
          label={t('cockpit_offers_kpi_top', 'Höchstes Angebot')}
        />
        {estKpi}
      </div>

      <div className="grid split g2">
        <section
          className="card"
          data-od-id="offers-trend"
        >
          <div className="sec">
            <h2>{t('cockpit_offers_trend', 'Verlauf der Angebote')}</h2>
            <span className="xs mu">
              {oldestFirst.length} {t('cockpit_offers_kpi_count', 'Angebote')}
            </span>
          </div>
          <div className="legend">
            <span>
              <svg
                width="18"
                height="8"
                aria-hidden="true"
              >
                <rect
                  width="18"
                  height="3"
                  y="2.5"
                  rx="1.5"
                  style={{ fill: 'var(--v3-pri)' }}
                />
              </svg>
              {t('cockpit_offers_kpi_count', 'Angebote')}
            </span>
            {est.value ? (
              <span>
                <svg
                  width="18"
                  height="8"
                  aria-hidden="true"
                >
                  <line
                    x1="0"
                    x2="18"
                    y1="4"
                    y2="4"
                    style={{
                      stroke: 'var(--v3-ok)',
                      strokeWidth: 2,
                      strokeDasharray: '5 3',
                    }}
                  />
                </svg>
                {t('cockpit_offers_kpi_estimate', 'Geschätzter Wert')}{' '}
                {fmtChf(est.value)}
              </span>
            ) : null}
          </div>
          <TrendChart
            points={points}
            reference={est.value}
            alt={trendAlt}
          />
        </section>

        <section
          className="card stack"
          style={{ gap: 12 }}
          data-od-id="asking-price-compare"
        >
          <h2>
            {t('cockpit_offers_compare', 'Vergleich zum Wunschpreis')}
            <NewBadge />
          </h2>
          <dl className="dl">
            <div>
              <dt>{t('cockpit_offers_asking', 'Verkaufspreis')}</dt>
              <dd>{fmtChf(asking)}</dd>
            </div>
            <div>
              <dt>{t('cockpit_offers_kpi_top', 'Höchstes Angebot')}</dt>
              <dd>{fmtChf(top)}</dd>
            </div>
          </dl>
          {asking && diff !== null ? (
            <Bars
              rows={[
                {
                  label: t('cockpit_offers_asking', 'Verkaufspreis'),
                  value: asking,
                  text: '100 %',
                },
                {
                  label: t('cockpit_offers_kpi_top', 'Höchstes Angebot'),
                  value: top,
                  text: `${diff >= 0 ? '+' : '−'}${Math.abs(diff)
                    .toFixed(1)
                    .replace('.', ',')} %`,
                },
              ]}
            />
          ) : null}
        </section>
      </div>

      <section
        className="card"
        data-od-id="offers-list"
        data-widget="cockpitOffersInDetailWidget"
      >
        <div className="sec">
          <h2>{t('cockpit_offers_list', 'Alle Angebote')}</h2>
          <span className="xs mu">
            {t('cockpit_offers_newest', 'neueste zuerst')}
          </span>
        </div>
        <table className="tbl">
          <thead>
            <tr>
              <th>{t('cockpit_offers_col_date', 'Datum')}</th>
              <th>{t('cockpit_offers_col_person', 'Person')}</th>
              <th className="r">{t('cockpit_offers_col_price', 'Angebot')}</th>
              <th>{t('cockpit_offers_col_status', 'Status')}</th>
              <th>{t('cockpit_offers_col_message', 'Nachricht')}</th>
              <th>{t('cockpit_offers_col_confirmation', 'Bestätigung')}</th>
            </tr>
          </thead>
          <tbody>
            {[...oldestFirst].reverse().map((o, i) => {
              const last = o.person?.split(/\s+/).pop()?.toLowerCase() ?? '';
              return (
                <tr key={`${o.date}-${i}`}>
                  <td data-l={t('cockpit_offers_col_date', 'Datum')}>
                    {fmtDate(o.date)}
                  </td>
                  <td data-l={t('cockpit_offers_col_person', 'Person')}>
                    <span className="who">
                      <b>{o.person}</b>
                      {isAdvisor ? (
                        <ContactActions
                          name={o.person}
                          phone={o.personPhone}
                          email={o.personEmail}
                        />
                      ) : null}
                    </span>
                  </td>
                  <td
                    className="r"
                    data-l={t('cockpit_offers_col_price', 'Angebot')}
                  >
                    <b>{fmtChf(o.offerPrice)}</b>
                  </td>
                  <td data-l={t('cockpit_offers_col_status', 'Status')}>
                    <span className="chip c-info">
                      <i />
                      {t('cockpit_offers_status_open', 'Offen')}
                    </span>
                  </td>
                  <td
                    className="msg"
                    data-l={t('cockpit_offers_col_message', 'Nachricht')}
                  >
                    {o.message || <span className="mu">–</span>}
                  </td>
                  <td
                    data-l={t('cockpit_offers_col_confirmation', 'Bestätigung')}
                  >
                    {o.offerConfirmationSecureAssetsPath ? (
                      <a
                        className="btn"
                        href={`${secureUrl}${o.offerConfirmationSecureAssetsPath}`}
                        target="_blank"
                        rel="noreferrer"
                        download
                        aria-label={`${t(
                          'cockpit_offers_confirmation_of',
                          'Angebotsbestätigung von',
                        )} ${o.person} ${t(
                          'cockpit_offers_download',
                          'herunterladen',
                        )}`}
                        data-od-id={`download-${last}`}
                      >
                        <Ico
                          name="doc"
                          size={16}
                        />
                        PDF
                      </a>
                    ) : (
                      <span className="mu">
                        {t('cockpit_offers_none', 'keine')}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>
    </V3Page>
  );
}
